<script setup>
import PrimaryButton from "../../../../frontend/src/components/ui/PrimaryButton.vue";
import {computed, reactive, ref, watch} from "vue";
import useForm from "@/composables/useForm";
import corporatesService from "@/services/corporates.service";
import enterprisesService from "@/services/enterprises.service";
import startupsService from "@/services/startups.service";
import ObjectSelector from "@/components/ui/ObjectSelector.vue";

const emit = defineEmits(['saved']);

const form = reactive({
    name: "",
    urlDomain: "",
    companyType: 'enterprise',
    companyId: null,
    mergeTo: null,
})

const company = ref(null);

const {populateForm, resetForm, flatErrors, setErrors, clearErrors} = useForm(form);
const showModal = ref(false);
const id = ref(null);
const loading = ref(false);
const merge = ref(false);
const mergeTo = ref(null);

const canSave = computed(() => form.name.length && form.urlDomain.length);

const getCompaniesList = computed(() => {
    if (form.companyType === 'enterprise') {
        return (query) => enterprisesService.search(query);
    } else {
        return (query) => startupsService.search(query);
    }
});

function getCorporatesList(query) {
    return corporatesService.search(query);
}

watch(() => form.companyType, () => {
    company.value = null;
});

watch(mergeTo, (value) => {
    form.mergeTo = value?.id || null;
});

watch(merge, (value) => {
    if (!value) {
        mergeTo.value = null;
    }
});

watch(company, (value) => {
    if (value) {
        form.companyId = value.id;
    } else {
        form.companyId = null;
    }
});

async function getData() {
    const {data} = await corporatesService.getEdit(id.value);
    populateForm(data);

    if (data.enterprise_id) {
        form.companyType = 'enterprise';
        let {data: enterpriseData} = await enterprisesService.getData(data.enterprise_id);
        company.value = enterpriseData;
    } else if (data.startup_id) {
        form.companyType = 'startup';
        let {data: startupData} = await startupsService.getData(data.startup_id);
        company.value = startupData;
    } else {
        company.value = null;
    }
}

async function save() {
    loading.value = true;
    clearErrors();

    try {
        const {data} = await corporatesService.save(id.value, form);

        if (!Object.keys(data.errors).length) {
            showModal.value = false;
            emit('saved');
        } else {
            setErrors(data.errors);
        }
    } finally {
        loading.value = false;
    }
}

function open(itemId) {
    resetForm();
    clearErrors();
    mergeTo.value = null;
    merge.value = false;

    if (itemId) {
        id.value = itemId;
        getData();
    } else {
        id.value = 0;
    }

    showModal.value = true;
}

defineExpose({open});
</script>

<template>
    <b-modal
        v-model="showModal"
        modal-class="modal-w-md" :title="(id ? 'Edit' : 'Add') + ' Feature'"
    >
        <form class="form pb-5">
            <template v-if="flatErrors.length">
                <div class="form__error" v-for="(error, index) in flatErrors" :key="index">{{error}}</div>
            </template>

            <div class="form__item">
                <h5 class="heading5 heading5--secondary">Name *</h5>
                <input class="form-control--mid form-control" type="text" v-model="form.name">
            </div>

            <div class="form__item">
                <h5 class="heading5 heading5--secondary">Domain *</h5>
                <input class="form-control--mid form-control" type="text" v-model="form.urlDomain">
            </div>

            <div class="form__item">
                <h5 class="heading5 heading5--secondary">Company Type</h5>

                <select class="form-control--mid form-control" v-model="form.companyType">
                    <option value="enterprise">Enterprise</option>
                    <option value="startup">Startup</option>
                </select>
            </div>

            <div class="form__item">
                <h5 class="heading5 heading5--secondary">
                    Select Company
                </h5>

                <ObjectSelector
                    v-model="company"
                    placeholder="Select Company"
                    :get-list-method="getCompaniesList"
                />
            </div>

            <div class="form__item">
                <label><input type="checkbox" v-model="merge"> Merge</label>

                <template v-if="merge">
                    <h5 class="heading5 heading5--secondary">
                        Select Corporate To Merge
                    </h5>

                    <ObjectSelector
                        v-model="mergeTo"
                        placeholder="Select Company"
                        :exclude-ids="[id]"
                        :get-list-method="getCorporatesList"
                    >
                        <template #option="props">
                            {{ props.option.name }} (#{{ props.option.id }})
                        </template>
                    </ObjectSelector>
                </template>
            </div>
        </form>

        <template #modal-footer>
            <PrimaryButton
                :disabled="!canSave"
                :loading="loading"
                @click="save"
            />
        </template>
    </b-modal>
</template>

<style scoped>

</style>
